import {
  Wallet,
  AlarmClock,
  Bell,
  Bot,
  Building,
  Building2,
  CalendarCog,
  CalendarRange,
  Clock,
  CreditCard,
  Crown,
  Database,
  DoorOpen,
  FileCheck,
  Gift,
  HeartPulse,
  Hospital,
  Layers,
  KeyRound,
  Link as LinkIcon,
  MessageCircle,
  Megaphone,
  Monitor,
  MonitorPlay,
  Banknote,
  Palette,
  Pill,
  Plug,
  Printer,
  Puzzle,
  Receipt,
  ScrollText,
  SlidersHorizontal,
  Stethoscope,
  User,
  UserRound,
  Users,
  type LucideIcon,
} from "lucide-react";

export type SettingsItem = {
  label: string;
  path: string;
  icon: LucideIcon;
  /**
   * Hide this item unless the signed-in user holds this permission (#18974).
   *
   * Presentation only — the endpoint behind the page enforces the same permission. Hiding a menu
   * nobody may use spares a receptionist a dead end; it is not what keeps them out.
   */
  requiresPermission?: string;
};
/** A settings category. `path` is the landing route (its first item) that the
 *  sidebar links to; `icon` is shown next to the category in the sidebar. */
export type SettingsGroup = {
  label: string;
  path: string;
  icon: LucideIcon;
  items: SettingsItem[];
};

/** Categories of the Settings hub. Each is a sub-item of the "Cài đặt" sidebar
 *  group (left column); its `items` fill the hub's middle column when active. */
export const settingsGroups: SettingsGroup[] = [
  /*
   * #19745 — ba màn quyết định giờ đặt lịch online, nay nằm CÙNG một nhóm.
   *
   * <p>Nguyên nhân gốc của #19688 không phải một luật sai mà là ba màn nằm ở hai nhóm menu khác
   * nhau: không ai nhìn thấy cả ba cùng lúc để đối chiếu, nên Thứ 7 thiếu Phân ca mà không ai
   * phát hiện cho tới khi QA báo.
   *
   * <p><b>{@code /settings/clinic} giải về nhóm này vì nó CHUYỂN hẳn sang đây, không phải vì
   * nhóm được khai trước.</b> Bản đầu của chú thích này nói thứ tự khai quyết định điều đó — sai,
   * và phép mutation vạch ra: dời nhóm xuống dưới "Phòng khám" mà không ca nào đỏ, bởi đường dẫn
   * ấy giờ chỉ thuộc đúng MỘT nhóm. Thứ thật sự giữ tính chất này là bất biến "mỗi đường dẫn chỉ
   * xuất hiện một lần" ({@code activeSettingsGroup} lấy nhóm khai trước, nên một mục nằm ở hai
   * nhóm sẽ dẫn người dùng vào cột của nhóm khác) — có ca test riêng cho nó.
   *
   * <p>Hệ quả là điều vé cần: người đang đứng ở chính trang Giờ làm việc — đúng ca của #19688 —
   * thấy ngay hai màn kia ở cột giữa.
   *
   * <p><b>Mỗi mục là một ROUTE RIÊNG, không phải tab trong một trang.</b> Đây là điều kiện anh Tỷ
   * chốt ngày 20/10: đổi "tab" phải điều hướng thật để {@link PlanRouteGuard} kiểm từng đường dẫn
   * độc lập. Bản trước (@a4052531@, đã bị lùi ở @113a359d@) gom ba màn vào MỘT route
   * {@code /settings/booking-hours}: cổng chỉ kiểm được một đường dẫn, nên phòng khám không mua
   * gói có "Buổi khám" vẫn thấy tab đó dựng ra và nhận một trang đầy 402. Nhóm menu không có
   * route riêng, nên nó cũng không sinh {@code Screen} mới và không chạm ADR-0053.
   *
   * <p>Việc "chỉ hiện mục thuộc gói" KHÔNG cần mã mới: {@link visibleSettingsItems} đã lọc theo
   * {@code allowsPath} cho cả thanh bên lẫn cột giữa.
   *
   * <p>#19978 — nhóm đổi tên "Đặt lịch" và nhận thêm hai màn từ Vận hành (Luồng đặt lịch, Giới
   * hạn), xếp theo THỨ TỰ CẤU HÌNH: giờ làm việc → khung giờ nhận lịch → luồng → giới hạn. Trước
   * đó nhóm tên "Cấu hình lịch đặt online" lại không chứa hai màn cốt lõi của đặt lịch. Vẫn chỉ là
   * dời mục menu: route giữ nguyên, không sinh {@code Screen} mới. "Lịch làm việc (bác sĩ)" KHÔNG
   * vào nhóm này — mỗi đường dẫn chỉ được ở MỘT nhóm; trang Khung giờ nhận lịch trỏ sang nó.
   */
  {
    label: "Đặt lịch",
    path: "/settings/clinic",
    icon: CalendarCog,
    items: [
      // Trang này mang CẢ thông tin chung, giờ làm việc và ngày nghỉ trong một form, một nút Lưu.
      // Nhãn nói đủ ba thứ vì nó chuyển cả trang vào đây, không tách khối giờ ra — tách là phải
      // thêm một đường lưu mới, đúng thứ vé cấm.
      { label: "Phòng khám · Giờ làm việc · Ngày nghỉ", path: "/settings/clinic", icon: Hospital },
      // Khung giờ đặt được (#19935) + Hình thức giờ. Lịch trực của bác sĩ ở Nhân sự & Quyền.
      { label: "Khung giờ nhận lịch", path: "/settings/sessions", icon: Clock },
      { label: "Luồng đặt lịch", path: "/settings/booking-policy", icon: CalendarCog },
      // Trang này còn mang Số thứ tự và Nhắc lịch (tách là vé riêng), nên nhãn nói đủ.
      { label: "Giới hạn & nhắc lịch", path: "/settings/booking-limits", icon: AlarmClock },
    ],
  },
  {
    label: "Phòng khám",
    path: "/settings/vertical",
    icon: Hospital,
    items: [
      // #19745 — "Phòng khám · Giờ làm việc · Ngày nghỉ" đã chuyển sang nhóm "Đặt lịch" (trước là
      // "Cấu hình lịch đặt online") ở trên. Giữ ở cả hai nhóm sẽ nhân đôi mục trong thanh bên, và vì
      // `activeSettingsGroup` lấy nhóm khai trước, mục ở đây sẽ dẫn vào cột của nhóm kia — một
      // mục bấm vào rồi thấy cột khác là thứ khó hiểu hơn cả vấn đề đang sửa.
      // CÁI GIÁ, nói thẳng: thông tin chung của phòng khám (tên, địa chỉ) nay nằm dưới một nhóm
      // tên "Đặt lịch". Nhãn của mục nói đủ ba thứ trang đó mang, nhưng nếu muốn
      // tách hẳn khối giờ ra khỏi trang Phòng khám thì đó là việc riêng (tách form + đường lưu).
      { label: "Ngành nghề & Gói mẫu", path: "/settings/vertical", icon: Puzzle },
      { label: "Chi nhánh", path: "/settings/branches", icon: Building2 },
      { label: "Thương hiệu", path: "/settings/branding", icon: Palette },
      // Tiền viện phí về đâu là cấu hình của phòng khám, không phải của thu ngân (#19010).
      { label: "Tài khoản nhận tiền", path: "/settings/bank-account", icon: Banknote },
      // Tầng 3: không bắt buộc, chỉ cho phòng khám cần hoàn tiền tự động (#18998).
      { label: "Cổng thanh toán", path: "/settings/payment-gateway", icon: Banknote },
      { label: "Mẫu in", path: "/settings/print-templates", icon: Printer },
      { label: "Hình thức khám", path: "/settings/exam-modes", icon: Stethoscope },
      { label: "Kê toa & Thuốc", path: "/settings/prescribing", icon: Pill },
      // ADR-0048 — first in the list would be truer to its weight, but it is a rare switch
      // and putting it above the daily settings would invite curious clicking.
      { label: "Chế độ vận hành", path: "/settings/operating-mode", icon: Hospital },
    ],
  },
  {
    label: "Danh mục",
    path: "/settings/departments",
    icon: Database,
    items: [
      { label: "Khoa", path: "/settings/departments", icon: Building },
      { label: "Buồng khám", path: "/settings/rooms", icon: DoorOpen },
      { label: "Nhóm dịch vụ", path: "/settings/service-categories", icon: Layers },
      { label: "Dịch vụ + Giá", path: "/settings/services", icon: HeartPulse },
      { label: "Gói liệu trình", path: "/settings/packages", icon: Gift },
      { label: "Hạng thành viên", path: "/settings/membership", icon: Crown },
      { label: "Thuốc + Catalog", path: "/settings/medicines", icon: Pill },
      { label: "Gợi ý triệu chứng", path: "/settings/symptoms", icon: Stethoscope },
    ],
  },
  {
    label: "Nhân sự & Quyền",
    path: "/settings/doctors",
    icon: Users,
    items: [
      { label: "Bác sĩ", path: "/settings/doctors", icon: UserRound },
      // #19937 — MỘT lịch làm việc: lịch trực (work_shift, trước là "Phân ca" ở Vận hành) và
      // ngày nghỉ phép của bác sĩ. Buổi khám (clinic_session) vẫn ở Vận hành.
      { label: "Lịch làm việc (bác sĩ)", path: "/settings/schedules", icon: CalendarRange },
      { label: "Người dùng", path: "/settings/users", icon: User },
      { label: "Phân quyền", path: "/settings/roles", icon: KeyRound },
    ],
  },
  {
    label: "Vận hành",
    path: "/settings/displays",
    icon: SlidersHorizontal,
    items: [
      // #19745 chuyển "Buổi khám" sang nhóm đặt lịch. #19937 gộp "Phân ca" vào "Lịch làm việc
      // (bác sĩ)" ở Nhân sự & Quyền. #19978 chuyển "Đặt lịch" (nay "Luồng đặt lịch") và "Giới hạn
      // & nhắc lịch" sang nhóm "Đặt lịch".
      { label: "Màn hình & thiết bị", path: "/settings/displays", icon: MonitorPlay },
      { label: "Màn hình kiosk", path: "/settings/kiosks", icon: Monitor },
      { label: "Gọi số & màn hình chờ", path: "/settings/queue-calling", icon: Megaphone },
      { label: "Thông báo", path: "/settings/notifications", icon: Bell },
      { label: "Biến nghiệp vụ", path: "/settings/notification-variables", icon: Bell },
      { label: "Sao lưu + Xuất", path: "/settings/backup", icon: Database },
      { label: "Audit log", path: "/settings/audit-log", icon: ScrollText },
      { label: "Đồng thuận PDPD", path: "/settings/consent", icon: FileCheck },
    ],
  },
  {
    label: "Nâng cao",
    path: "/settings/integrations",
    icon: Plug,
    items: [
      { label: "Tích hợp", path: "/settings/integrations", icon: Plug },
      { label: "Kết nối HIS", path: "/settings/his-integration", icon: LinkIcon },
      { label: "Kết nối Zalo", path: "/settings/zalo", icon: MessageCircle },
      { label: "Hóa đơn điện tử", path: "/settings/einvoice", icon: Receipt },
      { label: "Hoạt động AI", path: "/settings/ai-activity", icon: Bot },
      { label: "Gói dịch vụ", path: "/settings/subscription", icon: CreditCard },
      // Ví và thanh toán: tiền phòng khám trả cho Addy (#18974). Tách khỏi "Gói dịch vụ" vì đây
      // là nơi có TIỀN THẬT — nạp, sao kê, lịch sử — còn trang kia chỉ mô tả gói.
      {
        label: "Ví & Thanh toán",
        path: "/settings/billing",
        icon: Wallet,
        // A receptionist has no business reading the clinic's wallet, and no business spending it.
        requiresPermission: "saas.billing.read",
      },
    ],
  },
];

/** Điều một người dùng cụ thể của một phòng khám cụ thể được thấy. */
export type SettingsAudience = {
  /** Quyền của người đang đăng nhập; rỗng khi hồ sơ chưa tải xong. */
  permissions: string[];
  /** Gói của phòng khám có màn hình ở đường dẫn này không (ADR-0053). */
  allowsPath: (path: string) => boolean;
};

/**
 * Các mục của một nhóm Cài đặt mà người này thấy được: phải có trong gói của phòng khám VÀ, nếu
 * mục có gác quyền, người dùng phải giữ quyền đó.
 *
 * Thanh bên và cột giữa của hub Cài đặt cùng gọi hàm này. Hai bản lọc riêng là một ngày nào đó
 * thanh bên dẫn vào một nhóm mà cột giữa không còn mục nào.
 */
export function visibleSettingsItems(
  group: SettingsGroup,
  audience: SettingsAudience
): SettingsItem[] {
  return group.items.filter(
    (it) =>
      audience.allowsPath(it.path) &&
      (!it.requiresPermission || audience.permissions.includes(it.requiresPermission))
  );
}

/** The settings category that owns the given route (segment-boundary match), or
 *  undefined if the path isn't inside any category. */
export function activeSettingsGroup(pathname: string): SettingsGroup | undefined {
  return settingsGroups.find((g) =>
    g.items.some(
      (it) => pathname === it.path || pathname.startsWith(it.path + "/")
    )
  );
}
